import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { 
  ArrowLeft, 
  Loader2,
  CheckCircle,
  Package,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const AdminSeedProducts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const seedProducts = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch(`${API}/admin/seed-products`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        setError('Failed to seed products');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const defaultProducts = [
    { name: 'Happy Birthday Celebration Card', price: '$4.00', category: 'Birthday' },
    { name: 'Wedding Anniversary', price: '$4.00', category: 'Anniversary' },
    { name: 'Professional Business Card', price: '$19.99', category: 'Corporate' },
    { name: 'Christmas Card', price: '$4.00', category: 'Holiday' },
    { name: 'Thank You Card', price: '$4.00', category: 'Thank You' },
    { name: 'Congratulations Card', price: '$4.00', category: 'Celebration' },
    { name: 'Love & Romance Anniversary', price: '$4.00', category: 'Anniversary' },
    { name: 'Golden Anniversary Card', price: '$4.00', category: 'Anniversary' },
    { name: 'Custom Oreo Cookie Box', price: '$16.00', category: 'Cookies', special: true }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Seed Products</h1>
            <p className="text-sm text-gray-500">Add default products to the database</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Default Products
            </CardTitle>
            <CardDescription>
              These products will be added to your catalog if they don&apos;t already exist
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {defaultProducts.map((product, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    product.special ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      {product.special ? '🍪' : '🍫'}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.category}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-900">{product.price}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Result Message */}
        {result && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900">{result.message}</p>
                  <p className="text-sm text-green-700">
                    Added: {result.added} | Already existed: {result.skipped}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Card className="mb-6 bg-red-50 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <p className="font-semibold text-red-900">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Button */}
        <Button
          onClick={seedProducts}
          disabled={loading}
          className="w-full py-6 text-lg bg-amber-600 hover:bg-amber-700"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Adding Products...
            </>
          ) : (
            <>
              <Package className="w-5 h-5 mr-2" />
              Add Default Products
            </>
          )}
        </Button>

        <p className="text-center text-sm text-gray-500 mt-4">
          This will only add products that don&apos;t already exist. Existing products will not be modified.
        </p>
      </div>
    </div>
  );
};

export default AdminSeedProducts;
